<template>
	<div class="space-y-6">
		<!-- Header Section -->
		<div class="space-y-4">
			<div class="space-y-2">
				<h2 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
					{{ $t('onboarding.addChoresTitle') }}
				</h2>
				<div class="space-y-2 text-gray-700 dark:text-gray-300">
					<p>{{ $t('onboarding.addChoresDescription') }}</p>
					<p class="text-sm text-gray-600 dark:text-gray-400">
						{{ $t('onboarding.addChoresDescription2') }}
					</p>
				</div>
			</div>
		</div>

		<!-- Chore Selection Grid -->
		<div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
			<Message v-if="!filteredChoreSuggestions.length" severity="info" class="col-span-full">
				{{ $t('onboarding.noMoreChores') }}
			</Message>
			<ChoreSuggestionCard v-for="chore in filteredChoreSuggestions" :key="chore.name!" :chore="chore"
				v-model="chore.selected" />
		</div>

		<Divider />
		<!-- Action Footer -->
		<div class="flex items-center justify-between">
			<div class="text-sm text-gray-600 dark:text-gray-400">
				{{ $t('onboarding.selectedChores', selectedSuggestions.length) }}
			</div>
			<Button @click="handleNext" size="large"
				:icon="selectedSuggestions.length ? 'pi pi-plus' : 'pi pi-arrow-right'"
				:label="$t('onboarding.addChores', selectedSuggestions.length)" />
		</div>
	</div>
</template>

<script setup lang="ts">
import { useChoreSettingsStore } from "@/stores";
import { storeToRefs } from "pinia";
import Button from 'primevue/button';
import { computed, ref, type Ref } from "vue";
import ChoreSuggestionCard from './ChoreSuggestionCard.vue';
import type { NewChore } from "@/types";
import { DifficultyLevel, RecurrenceIntervalType, type CreateInitialChoreDto } from "@/client";

const emit = defineEmits<{
	(e: 'next', chores: CreateInitialChoreDto[]): void
}>();

const { choreTemplates } = storeToRefs(useChoreSettingsStore());

const filteredChoreSuggestions = computed(() => {
	// filter out chores that already exist in the store
	return choreSuggestions.value.filter(suggestion => {
		return !choreTemplates.value.find(chore => chore.name === suggestion.name);
	});
});


const choreSuggestions: Ref<NewChore[]> = ref<NewChore[]>([
	{
		name: "Bad putzen 🚿",
		description: "Waschbecken, Toilette, Dusche und Badewanne putzen",
		autoSchedule: true,
		recurrenceInterval: RecurrenceIntervalType.WEEKLY,
		estimatedMinutes: 45,
		difficultyLevel: DifficultyLevel.VERY_HARD,
		categoryName: "Putzen",
		selected: false
	},
	{
		name: "Bettwäsche wechseln 🛏️",
		description: "Bettwäsche abziehen und neu beziehen",
		autoSchedule: false,
		estimatedMinutes: 15,
		difficultyLevel: DifficultyLevel.EASY,
		categoryName: "Wäsche",
		selected: false
	},
	{
		name: "Wocheneinkauf 🛒",
		description: "Wöchentlicher Großeinkauf",
		autoSchedule: true,
		recurrenceInterval: RecurrenceIntervalType.WEEKLY,
		estimatedMinutes: 70,

		difficultyLevel: DifficultyLevel.MEDIUM,
		categoryName: "Einkaufen",
		selected: false
	},
	{
		name: "Kochen 🍳",
		description: "Kochen für alle (egal wie räudig)",
		autoSchedule: false,

		estimatedMinutes: 45,

		difficultyLevel: DifficultyLevel.MEDIUM,
		categoryName: "Kochen",
		selected: false
	},
	{
		name: "Küche aufräumen 🍽️",
		description: "Spülmaschine einräumen und ausräumen, Arbeitsflächen abwischen",
		autoSchedule: false,

		estimatedMinutes: 20,

		difficultyLevel: DifficultyLevel.EASY,
		categoryName: "Aufräumen",
		selected: false
	},
	{
		name: "Müll rausbringen 🗑️",
		description: "Müllsäcke in die Tonne bringen",
		autoSchedule: false,

		estimatedMinutes: 10,

		difficultyLevel: DifficultyLevel.EASY,
		categoryName: "Aufräumen",
		selected: false
	},
	{
		name: "Staubsaugen 🧹",
		description: "Staubsaugen in allen Räumen",
		autoSchedule: true,
		recurrenceInterval: RecurrenceIntervalType.WEEKLY,

		estimatedMinutes: 60,

		difficultyLevel: DifficultyLevel.MEDIUM,
		categoryName: "Putzen",
		selected: false
	},
	{
		name: "Pflanzen gießen 🌱",
		description: "Alle Pflanzen gießen",
		autoSchedule: false,

		estimatedMinutes: 10,

		difficultyLevel: DifficultyLevel.EASY,
		categoryName: "Garten",
		selected: false
	},
	{
		name: "Spülmaschine ausräumen 🍽️",
		description: "Spülmaschine ausräumen",
		autoSchedule: false,

		estimatedMinutes: 10,

		difficultyLevel: DifficultyLevel.EASY,
		categoryName: "Aufräumen",
		selected: false
	},
	{
		name: "Staub wischen 🧹",
		description: "Staub wischen in allen Räumen",
		autoSchedule: true,
		recurrenceInterval: RecurrenceIntervalType.BI_WEEKLY,

		estimatedMinutes: 30,

		difficultyLevel: DifficultyLevel.MEDIUM,
		categoryName: "Putzen",
		selected: false
	},
	{
		name: "Wäsche abhängen 🧺",
		description: "Wäsche abhängen und zusammenlegen",
		autoSchedule: false,

		estimatedMinutes: 20,

		difficultyLevel: DifficultyLevel.MEDIUM,
		categoryName: "Wäsche",
		selected: false
	},
	{
		name: "Wäsche aufhängen 🧺",
		description: "Wäsche aufhängen",
		autoSchedule: false,

		estimatedMinutes: 12,

		difficultyLevel: DifficultyLevel.MEDIUM,
		categoryName: "Wäsche",
		selected: false
	},
	{
		name: "Wäsche waschen 🧺",
		description: "Wäsche sortieren, in die Waschmaschine geben und starten",
		autoSchedule: false,

		estimatedMinutes: 7,

		difficultyLevel: DifficultyLevel.EASY,
		categoryName: "Wäsche",
		selected: false
	}
]);

const selectedSuggestions = computed(() => choreSuggestions.value.filter(c => c.selected));

const handleNext = async () => {
	emit('next', selectedSuggestions.value);
};

</script>
